import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  CardFooter,
} from "reactstrap";
import { configureToastr, triggerToast } from '../../utils/toast'
import { loadStripe } from '@stripe/stripe-js';
import { userProfileApi } from "../../services/userProfileApi";
import { clearPostPaymentRedirect } from "../../utils/loginRedirect";
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics";
import "./PlansComponent.css"
import SkeletonCard from "../KeywordTracking/components/SkeletonCard";
import UpgradePlanModal from "../../components/Common/Modal/UpgradePlanModal";

// Abbreviation tooltips for benefits
const ABBREVIATION_TOOLTIPS = {
  KTR: 'Keyword Relevance',
  AUR: 'Average User Rating',
  URV: 'User Reviews Volume',
  // Common abbreviations seen in UI — add meanings here as needed
  ASR: 'App Store Rank',
  KU: 'Keyword Usage',
};

// Safely escape strings for use in RegExp
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const renderBenefitValueWithTooltips = (value) => {
  const text = typeof value === 'string' ? value : String(value ?? '');

  // Build a dynamic, case-insensitive regex from ABBREVIATION_TOOLTIPS keys
  const keys = Object.keys(ABBREVIATION_TOOLTIPS);
  if (keys.length === 0) {
    return <span className="benefit-text">{text}</span>;
  }

  // Sort by length desc to prefer longer matches first, and escape special chars
  const pattern = keys
    .map(k => escapeRegExp(k))
    .sort((a, b) => b.length - a.length)
    .join('|');

  const parts = text.split(new RegExp(`(${pattern})`, 'gi'));

  return (
    <span className="benefit-text">
      {parts.map((part, idx) => {
        const key = typeof part === 'string' ? part.toUpperCase() : part;
        if (ABBREVIATION_TOOLTIPS[key]) {
          return (
            <abbr
              key={idx}
              title={ABBREVIATION_TOOLTIPS[key]}
              style={{ textDecoration: 'underline dotted', cursor: 'help' }}
            >
              {part}
            </abbr>
          );
        }
        return <React.Fragment key={idx}>{part}</React.Fragment>;
      })}
    </span>
  );
};

// Helper utilities for price display
const formatPriceDisplay = (price) => {
  const p = Number(price);
  return p === 0 ? 'Free' : `$${price}`;
};

const isFreePrice = (price) => Number(price) === 0;

const STRIPE_PUBLIC_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_live_51RT5QxLDJ9fsfGZRrpY58fAeZcrHLTVnEFd9Gpn5bdw0qyLNSfN6UIFPKSnBMEk9MFK5Un2zn3gXh3VOoLzIA3kk00Tsk7I9KA';
const loadStripeManually = () => {
  return new Promise((resolve, reject) => {
    // Check if Stripe is already available
    if (window.Stripe) {
      resolve(window.Stripe(STRIPE_PUBLIC_KEY));
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if (window.Stripe) {
          resolve(window.Stripe(STRIPE_PUBLIC_KEY));
        } else {
          reject(new Error('Stripe object not available after script load'));
        }
      });
      existingScript.addEventListener('error', () => {
        reject(new Error('Failed to load existing Stripe script'));
      });
      return;
    }

    // Create and load new script
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      // Add a small delay to ensure Stripe object is available
      setTimeout(() => {
        if (window.Stripe) {
          resolve(window.Stripe(STRIPE_PUBLIC_KEY));
        } else {
          reject(new Error('Stripe script loaded but object not available'));
        }
      }, 100);
    };

    script.onerror = (error) => {
      console.error('Stripe script load error:', error);
      reject(new Error('Failed to load Stripe script'));
    };

    document.head.appendChild(script);
  });
};

// Reusable PlanCard Component (kept for reference; not used in grid layout)
const PlanCard = ({
  title,
  price,
  benefits,
  allBenefitNames,
  buttonText,
  buttonAction,
  isPopular = false,
  isDisabled = false,
  loading = false,
  stripeLoaded = false
}) => {
  // Helper function to get benefit value for a specific benefit name
  const getBenefitValue = (benefitName) => {
    if (benefits && Array.isArray(benefits)) {
      const benefit = benefits.find(b => b.benefit_name === benefitName);
      return benefit ? benefit : { state: 'negative', benefit_value: 'Not available' };
    }
    return { state: 'negative', benefit_value: 'Not available' };
  };

  return (
    <div className="plan-card">
      {/* Header spacer with inline popular badge */}
      <div className="plan-header-spacer">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <h5 className="plan-title" style={{ margin: 0 }}>
            {title}
          </h5>
          {isPopular && (
            <div className="popular-badge">Most Popular</div>
          )}
        </div>

        <div className="plan-price-container">
          <div className="plan-price-row">
            <span className="plan-price">{formatPriceDisplay(price)}</span>
            {!isFreePrice(price) && (
              <span className="plan-period">/month</span>
            )}
          </div>
        </div>
      </div>

      <Button
        className={`plan-button ${buttonText === 'Upgrade' || buttonText === 'Current Plan' ? 'plan-button--upgrade' : ''}`}
        onClick={buttonAction}
        disabled={isDisabled || loading || !stripeLoaded}
      >
        {loading ? 'Processing...' : buttonText}
      </Button>

      <div className="plan-benefits">
        {allBenefitNames.map((benefitName, index) => {
          const benefit = getBenefitValue(benefitName);
          return (
            <div key={index} className="plan-benefit-item">
              <span className={`benefit-icon ${benefit.state}`}>
                {benefit.state === 'positive' ? (
                  <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M15.9422 1.06719L5.94219 11.0672C5.82496 11.1845 5.66588 11.2505 5.5 11.2505C5.33412 11.2505 5.17504 11.1845 5.05781 11.0672L0.682812 6.69219C0.438599 6.44797 0.438599 6.05203 0.682812 5.80781C0.927026 5.5636 1.32297 5.5636 1.56719 5.80781L5.5 9.74141L15.0578 0.182813C15.302 -0.0614009 15.698 -0.0614009 15.9422 0.182813C16.1864 0.427026 16.1864 0.822974 15.9422 1.06719Z" fill="#4CAF50" />
                  </svg>
                ) : (
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M13.2071 3.79289C13.5976 4.18342 13.5976 4.81658 13.2071 5.20711L10.4142 8L13.2071 10.7929C13.5976 11.1834 13.5976 11.8166 13.2071 12.2071C12.8166 12.5976 12.1834 12.5976 11.7929 12.2071L9 9.41421L6.20711 12.2071C5.81658 12.5976 5.18342 12.5976 4.79289 12.2071C4.40237 11.8166 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 4.18342 4.79289 3.79289C5.18342 3.40237 5.81658 3.40237 6.20711 3.79289L9 6.58579L11.7929 3.79289C12.1834 3.40237 12.8166 3.40237 13.2071 3.79289Z" fill="#F44336" />
                  </svg>
                )}
              </span>
              {renderBenefitValueWithTooltips(benefit.benefit_value)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function PlansComponent() {
  const { trackEvent } = useAnalytics();
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [loadingStates, setLoadingStates] = useState({}); // Track loading per plan
  const [networkTest, setNetworkTest] = useState('Testing...');
  const [loadingError, setLoadingError] = useState(null);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] = useState(null);
  const [currentSubscriptionEndDate, setCurrentSubscriptionEndDate] = useState(null);
  const [currentSubscriptionStartDate, setCurrentSubscriptionStartDate] = useState(null);
  const [allBenefitNames, setAllBenefitNames] = useState([]);
  const [visiblePlanIndex, setVisiblePlanIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [currentSubscriptionId, setCurrentSubscriptionId] = useState(null);
  const [currentPlanLoading, setCurrentPlanLoading] = useState(true);

  // Modal state for cancellation confirmation
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelPlanData, setCancelPlanData] = useState(null);

  // Modal state for upgrade confirmation
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradePlanData, setUpgradePlanData] = useState(null);

  // Flag to prevent unnecessary API calls during cancellation
  const [isCancelling, setIsCancelling] = useState(false);

  // Monitor Stripe state changes
  useEffect(() => {
    // Stripe state monitoring (commented out for production)
  }, [stripeLoaded, stripeInstance]);

  // Mobile breakpoint detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch current user's plan
  const fetchCurrentPlan = async () => {
    try {
      setCurrentPlanLoading(true);
      const result = await userProfileApi.fetchCurrentPlan();

      if (result.data) {
        // If product is null, user has default plan (Explorer)
        // If product has data, user has that specific plan
        const planId = result.data.product;
        const subscriptionId = result.data.subscription_id || result.data.id || null;
        const subscriptionStatus = result.data.status || result.data.subscription_status || null;
        const subscriptionEnd = result.data.end_date || result.data.trial_end || result.data.expires_at || null;
        const subscriptionStart = result.data.start_date || result.data.trial_start || result.data.started_at || null;

        setCurrentSubscriptionStatus(subscriptionStatus);
        setCurrentSubscriptionEndDate(subscriptionEnd);
        setCurrentSubscriptionStartDate(subscriptionStart);

        // Check if subscription is still active (not cancelled) or cancelled but still active
        // If subscription_id is null or empty, the subscription is cancelled
        const isSubscriptionActive = subscriptionId &&
          subscriptionId !== null &&
          subscriptionStatus !== 'cancelled' &&
          subscriptionStatus !== 'canceled';

        // Handle cancelled_but_active status - keep plan active so "Ends on" shows
        const isCancelledButActive = subscriptionStatus === 'cancelled_but_active';

        // Set current plan if subscription is active OR cancelled but still active
        if (isSubscriptionActive || isCancelledButActive) {
          setCurrentPlanId(planId);
          setCurrentSubscriptionId(subscriptionId);
        } else {
          // Subscription is fully cancelled, reset to default plan
          setCurrentPlanId(null);
          setCurrentSubscriptionId(null);
        }
      }
    } catch (error) {
      console.error('Error fetching current plan:', error);
      // If error, assume user has default plan
      setCurrentPlanId(null);
      setCurrentSubscriptionId(null);
    } finally {
      setCurrentPlanLoading(false);
    }
  };

  // Fetch plans from API
  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const result = await userProfileApi.fetchPlans();

      if (result.data && Array.isArray(result.data)) {
        // ✅ Check if user has a paid plan (Basic or Premium)
        const hasPaidPlan = currentPlanId !== null && currentSubscriptionId !== null;

        // ✅ Check if subscription is cancelled but still active
        const isCancelledButActive = currentSubscriptionStatus === 'cancelled_but_active';

        // ✅ Check if cancelled plan has ended (end_date has passed)
        const hasCancelledPlanEnded = isCancelledButActive && currentSubscriptionEndDate
          ? new Date(currentSubscriptionEndDate).getTime() <= Date.now()
          : false;

        // ✅ Detect Basic / Premium plans and whether the user is currently on Premium
        const basicPlanFromApi = result.data.find(
          (plan) => plan.name && plan.name.toLowerCase().includes('basic')
        );
        const premiumPlanFromApi = result.data.find(
          (plan) => plan.name && plan.name.toLowerCase().includes('premium')
        );

        const isUserOnPremium =
          !!premiumPlanFromApi &&
          premiumPlanFromApi.id === currentPlanId &&
          currentSubscriptionId !== null;

        // Collect all unique benefit names from all plans (keep API order)
        const benefitNamesSet = new Map();
        result.data.forEach(plan => {
          if (plan.description && Array.isArray(plan.description)) {
            plan.description.forEach((benefit, idx) => {
              if (!benefitNamesSet.has(benefit.benefit_name)) {
                benefitNamesSet.set(benefit.benefit_name, idx);
              }
            });
          }
        });
        const allNames = Array.from(benefitNamesSet.keys());
        setAllBenefitNames(allNames);

        // Transform API data to match our component structure
        // We'll also capture the current paid plan so we can trigger the Upgrade/Downgrade modal
        let currentPlanForUpgrade = null;

        const transformedPlans = result.data.map((plan, index) => {
          // ✅ Determine if this plan is current
          const isCurrentPlan =
            (currentPlanId === plan.id ||
              (currentPlanId && result.data.current_plan_name === plan.name)) &&
            currentSubscriptionId !== null;

          // ✅ Special handling for Explorer (free plan)
          let isExplorerCurrent = false;
          if (plan.is_default) {
            if (hasPaidPlan) {
              // User has paid → Explorer disabled
              isExplorerCurrent = false;
            } else {
              // User has no paid plan → Explorer is current
              isExplorerCurrent = true;
            }
          }

          // ✅ Disable if:
          //  - This plan is already current
          //  - Explorer and user already has a paid plan
          //  - Explorer is current (free default plan)
          //  - User is on Premium and this is the Basic plan (prevent downgrading via checkout)
          //  - User has a cancelled but active plan that hasn't ended yet (disable all other plans)
          let isDisabled =
            isCurrentPlan || (plan.is_default && hasPaidPlan) || isExplorerCurrent;

          const isBasicPlan =
            basicPlanFromApi && plan.id === basicPlanFromApi.id;

          if (isUserOnPremium && isBasicPlan) {
            isDisabled = true;
          }

          // If user has a cancelled but active plan that hasn't ended, disable all other plans
          if (isCancelledButActive && !hasCancelledPlanEnded && !isCurrentPlan) {
            isDisabled = true;
          }

          // Use description array if available, otherwise fallback to all_benefits
          let benefits = [];
          if (plan.description && Array.isArray(plan.description)) {
            benefits = plan.description;
          } else if (plan.all_benefits && Array.isArray(plan.all_benefits)) {
            benefits = plan.all_benefits.map(benefit => ({
              state: "positive",
              benefit_name: benefit,
              benefit_value: benefit,
            }));
          }

          // Determine benefits list once so it can be reused for upgrade modal
          // (for both display and upgrade/downgrade comparison)
          let buttonText, buttonAction;

          // Capture the current paid plan for use in the upgrade/downgrade modal
          if (isCurrentPlan && !plan.is_default && !isFreePrice(plan.price)) {
            currentPlanForUpgrade = {
              id: plan.id,
              title: plan.name,
              price: plan.price,
              benefits,
              subscriptionId: currentSubscriptionId,
            };
          }

          // Determine button text and action for current paid plans
          if (isCurrentPlan || isExplorerCurrent) {
            buttonText = "Current Plan";
            buttonAction = null;
          } else {
            const isPaidPlan = !isFreePrice(plan.price);

            // If user already has a paid plan and this is another *more expensive* paid (non-default) plan,
            // open the Upgrade modal instead of sending them to Stripe checkout.
            // Downgrades (cheaper plans) are not allowed here.
            if (
              hasPaidPlan &&
              isPaidPlan &&
              !plan.is_default &&
              currentPlanForUpgrade &&
              Number(plan.price) > Number(currentPlanForUpgrade.price)
            ) {
              buttonText = 'Upgrade';
              buttonAction = () => {
                openUpgradeModal(currentPlanForUpgrade, {
                  id: plan.id,
                  title: plan.name,
                  price: plan.price,
                  benefits,
                });
              };
            } else {
              // Default behavior: show Free label for free plans, 'Upgrade' for paid plans
              buttonText = isFreePrice(plan.price) ? 'Free Plan' : 'Upgrade';
              buttonAction = () => handleClick(plan.id, plan.name);
            }
          }

          return {
            id: plan.id,
            title: plan.name,
            price: plan.price,
            benefits: benefits,
            buttonText: buttonText,
            buttonAction: buttonAction,
            isDisabled: isDisabled, // ✅ properly disables button
            isPopular: index === 1,
            isDefault: isCurrentPlan || isExplorerCurrent,
            isFreePlan: plan.is_default,
            subscriptionId: isCurrentPlan ? currentSubscriptionId : null,
            // Free trial metadata (if provided by API)
            freeTrialDays: plan.free_trial_days,
            eligibleFreeTrial: plan.eligible_free_trial,
          };
        });

        setPlans(transformedPlans);
        setVisiblePlanIndex(0);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlansError(error.message);
    } finally {
      setPlansLoading(false);
    }
  };



  // Fetch current plan first
  useEffect(() => {
    fetchCurrentPlan();
  }, []);

  // Fetch plans after current plan data is available
  useEffect(() => {
    if (!currentPlanLoading && !isCancelling && (currentPlanId !== undefined || currentSubscriptionId !== undefined)) {
      fetchPlans();
    }
  }, [currentPlanLoading, currentPlanId, currentSubscriptionId, isCancelling]);

  useEffect(() => {
    const testNetwork = async () => {
      try {
        const response = await fetch('https://js.stripe.com/v3/', { method: 'HEAD' });
        if (response.ok) {
          setNetworkTest('✅ Network can reach Stripe CDN');
        } else {
          setNetworkTest(`❌ Cannot reach Stripe CDN (Status: ${response.status})`);
        }
      } catch (error) {
        setNetworkTest(`❌ Network error: ${error.message}`);
      }
    };
    testNetwork();
  }, []);

  useEffect(() => {
    const initStripe = async (retryCount = 0) => {
      try {
        // Try to load Stripe using the official package first
        const stripe = await loadStripe(STRIPE_PUBLIC_KEY);

        if (stripe) {
          setStripeInstance(stripe);
          setStripeLoaded(true);
          setLoadingError(null);
          return;
        }
      } catch (error) {
        console.error('❌ Stripe package load failed:', error);
      }

      try {
        // Fallback to manual loading
        const manual = await loadStripeManually();

        if (manual) {
          setStripeInstance(manual);
          setStripeLoaded(true);
          setLoadingError(null);
        } else {
          throw new Error('Manual Stripe load returned null');
        }
      } catch (err) {
        console.error('❌ Stripe initialization failed:', err);
        setStripeLoaded(false);
        setLoadingError(err.message);

        // Retry once after a delay if this is the first attempt
        if (retryCount === 0) {
          setTimeout(() => {
            initStripe(1);
          }, 2000);
        }
      }
    };

    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initStripe();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleClick = async (productId, planName) => {
    // Get the actual Stripe instance (handle hot reload timing issues)
    let currentStripe = stripeInstance;

    if (!currentStripe && window.Stripe) {
      currentStripe = window.Stripe(STRIPE_PUBLIC_KEY);
    }

    // Check if we have a valid Stripe instance
    if (!currentStripe) {
      await new Promise(resolve => setTimeout(resolve, 500));

      // Try again after delay
      if (!currentStripe && window.Stripe) {
        currentStripe = window.Stripe(STRIPE_PUBLIC_KEY);
      }

      if (!currentStripe) {
        const errorMsg = loadingError
          ? `Payment system error: ${loadingError}. Please refresh the page.`
          : 'Payment system is still loading. Please wait a moment and try again.';
        alert(errorMsg);
        return;
      }
    }

    // If we have a valid Stripe instance, proceed with payment
    setLoadingStates(prev => ({ ...prev, [productId]: true }));
    trackEvent(ANALYTICS_EVENTS.CONVERSION.PLAN_UPGRADE_STARTED, { plan_id: productId, plan_name: planName, trigger: "profile_plans" });

    try {
      const data = await userProfileApi.createPaymentSession(productId);

      if (!data.session_id) {
        throw new Error('No session_id in response');
      }

      // A checkout started here should land on the normal success page, not
      // follow a return path left behind by an abandoned report checkout.
      clearPostPaymentRedirect();
      const { error } = await currentStripe.redirectToCheckout({
        sessionId: data.session_id
      });

      if (error) {
        throw new Error(`Payment redirect failed: ${error.message}`);
      }

    } catch (err) {
      console.error('Payment error:', err);
      // initialize toasts (safe to call multiple times)
      try { configureToastr() } catch (e) { /* ignore */ }

      // API returns JSON error text like: {"error":"You already have an active subscription"}
      const msg = String(err.message || err || '');
      if (msg.includes('You already have an active subscription')) {
        try { triggerToast('You already have an active subscription', 'warning') } catch (e) { }
      } else {
        try { triggerToast(`Payment error: ${msg}`, 'error') } catch (e) { }
      }
    } finally {
      setLoadingStates(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async (subscriptionId, planName) => {
    setLoadingStates(prev => ({ ...prev, [`cancel_${subscriptionId}`]: true }));
    setIsCancelling(true); // Set flag to prevent fetching plans

    try {
      // Call API to cancel subscription
      await userProfileApi.cancelSubscription(subscriptionId);

      // Immediately reset current plan state to show "Buy" button
      setCurrentPlanId(null);
      setCurrentSubscriptionId(null);

      // Immediately refresh plans to show updated UI
      await fetchPlans();

      // Close the modal
      setShowCancelModal(false);
      setCancelPlanData(null);

      // Show success toast instead of alert
      // Note: You'll need to implement toast functionality or use a library like react-toastify
      // For now, using a simple success message that can be replaced with toast
      const successMessage = 'Subscription cancelled successfully. You will continue to have access until the end of your current billing period.';
      // Replace this with your toast implementation
    } catch (err) {
      console.error('Cancellation error:', err);
      // Show error toast instead of alert
      const errorMessage = `Failed to cancel subscription: ${err.message}`;
      // Replace this with your toast implementation
    } finally {
      setLoadingStates(prev => ({ ...prev, [`cancel_${subscriptionId}`]: false }));
      setIsCancelling(false); // Reset flag after cancellation
    }
  };

  // Open cancellation modal
  const openCancelModal = (subscriptionId, planName) => {
    setCancelPlanData({ subscriptionId, planName });
    setShowCancelModal(true);
  };

  // Open upgrade modal (fetches Stripe proration preview before showing)
  const openUpgradeModal = async (currentPlan, targetPlan) => {
    if (!currentPlan?.subscriptionId || !targetPlan?.id) {
      setUpgradePlanData({ currentPlan, targetPlan, preview: null });
      setShowUpgradeModal(true);
      return;
    }

    try {
      const preview = await userProfileApi.getUpgradePreview(
        currentPlan.subscriptionId,
        targetPlan.id
      );

      setUpgradePlanData({ currentPlan, targetPlan, preview });
      setShowUpgradeModal(true);
    } catch (error) {
      console.error('Error fetching upgrade preview:', error);
      setUpgradePlanData({ currentPlan, targetPlan, preview: null });
      setShowUpgradeModal(true);
    }
  };

  // Handle plan upgrade/downgrade
  const handlePlanUpgrade = async (currentPlan, targetPlan) => {
    setLoadingStates(prev => ({ ...prev, [`upgrade_${targetPlan.id}`]: true }));

    try {
      // Call API to upgrade/downgrade subscription
      await userProfileApi.upgradeSubscription(currentPlan.subscriptionId, targetPlan.id);

      // Close the modal
      setShowUpgradeModal(false);
      setUpgradePlanData(null);

      // Refresh current plan and plans data
      await fetchCurrentPlan();
      await fetchPlans();

      // Show success message
      const successMessage = `Successfully ${targetPlan.price > currentPlan.price ? 'upgraded' : 'downgraded'} to ${targetPlan.title} plan.`;

    } catch (err) {
      console.error('Upgrade error:', err);
      const errorMessage = `Failed to ${targetPlan.price > currentPlan.price ? 'upgrade' : 'downgrade'} plan: ${err.message}`;
    } finally {
      setLoadingStates(prev => ({ ...prev, [`upgrade_${targetPlan.id}`]: false }));
    }
  };

  // Helper to get a plan's benefit by name
  const findBenefitForPlan = (plan, benefitName) => {
    if (plan && plan.benefits && Array.isArray(plan.benefits)) {
      const found = plan.benefits.find(b => b.benefit_name === benefitName);
      return found ? found : { state: 'negative', benefit_value: 'Not available' };
    }
    return { state: 'negative', benefit_value: 'Not available' };
  };

  // Helper to compute remaining trial days for the *current* subscription
  const getCurrentTrialInfoForPlan = (plan) => {
    try {
      if (
        !plan ||
        plan.id !== currentPlanId ||
        currentSubscriptionStatus !== 'trialing' ||
        !currentSubscriptionEndDate
      ) {
        return null;
      }

      const endMs = new Date(currentSubscriptionEndDate).getTime();
      const nowMs = Date.now();
      if (!Number.isFinite(endMs) || endMs <= nowMs) {
        return null;
      }

      const DAY_MS = 24 * 60 * 60 * 1000;

      // Days left (ceil so partial day counts as a full remaining day)
      const daysLeft = Math.max(0, Math.ceil((endMs - nowMs) / DAY_MS));

      // Total trial length
      let totalDays = plan.freeTrialDays ? Number(plan.freeTrialDays) : null;

      // If backend provides a start_date, derive total days from it
      if (!totalDays && currentSubscriptionStartDate) {
        const startMs = new Date(currentSubscriptionStartDate).getTime();
        if (Number.isFinite(startMs) && startMs < endMs) {
          totalDays = Math.max(1, Math.ceil((endMs - startMs) / DAY_MS));
        }
      }

      // Fallback to 7 days if nothing else is available
      if (!totalDays || !Number.isFinite(totalDays)) {
        totalDays = 7;
      }

      return { daysLeft, totalDays };
    } catch (e) {
      return null;
    }
  };

  const goPrevPlan = () => setVisiblePlanIndex(i => Math.max(0, i - 1));
  const goNextPlan = () => setVisiblePlanIndex(i => Math.min(plans.length - 1, i + 1));

  if (plansLoading || currentPlanLoading) {
    return (
      <div className="plans-container">
        <div className="plans-skeleton-cards">
          {[...Array(2)].map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        </div>
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="plans-container">
        <Alert color="danger">
          <h4 className="alert-heading">Error Loading Plans</h4>
          <p>{plansError}</p>
          <hr />
          <p className="mb-0">
            Please refresh the page or contact support if the problem persists.
          </p>
        </Alert>
      </div>
    );
  }
  const isComingSoonFeature = (benefitName, benefit) => {
    // Defensive checks
    if (!benefitName || typeof benefitName !== 'string') return false;

    const name = benefitName.toLowerCase().trim();

    // Explicit exceptions (never show Coming Soon for these exact features)
    const exceptions = [
      'auto reports & alerts',
      'auto reports',
      'reports & alerts',
      // Exclude keyword/category rank rows from coming-soon
      'keyword & category rank',
      'keyword & category ranks',
      'keyword & category rank(s)',
      'keyword & category',
      'keyword & category rank(s)',
      // Exclude sentiment analysis from coming-soon
      'sentiment analysis',
      'sentiment',
      'ai sentiment analysis'
    ];

    if (exceptions.includes(name)) return false;

    // Honor explicit coming_soon flag from the API (unless the feature is in exceptions)
    if (benefit && (benefit.coming_soon === true || benefit.comingSoon === true)) return true;

    // Also treat explicit 'coming soon' markers in benefit values as coming soon
    if (
      benefit &&
      benefit.benefit_value &&
      typeof benefit.benefit_value === 'string' &&
      benefit.benefit_value.toLowerCase().includes('coming soon')
    ) {
      return true;
    }

    return false;
  };

  // Coming Soon badge component for reuse
  const ComingSoonBadge = ({ isMobile = false }) => (
    <div style={{
      background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
      color: 'white',
      fontSize: isMobile ? '7px' : '8px',
      fontWeight: '600',
      padding: isMobile ? '0px 2px' : '0px 3px',
      borderRadius: isMobile ? '3px' : '4px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      alignSelf: 'flex-start',
      boxShadow: '0 1px 2px rgba(255, 107, 53, 0.18)',
      marginBottom: '0'
    }}>
      Coming Soon
    </div>
  );



  return (
    <div className="plans-container">
      {!stripeLoaded && !loadingError && (
        <div className="text-center mb-3">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading Stripe...</span>
          </div>
          <span className="ms-2">Loading payment system...</span>
        </div>
      )}

      {loadingError && (
        <Alert color="warning" className="mb-3">
          <strong>Payment System Warning:</strong> {loadingError}
          <br />
          <small>You can still view plans, but payments may not work. Please refresh the page.</small>
        </Alert>
      )}

      {/* Desktop: original grid comparison showing all plans */}
      {!isMobile && (
        <div
          className="plans-grid"
          style={{ gridTemplateColumns: `220px repeat(${plans.length}, 1fr)` }}
        >
          {/* Header Row */}
          <div className="cell feature-header"><h5>Features</h5></div>
          {plans.map((plan) => (
            <div key={plan.id} className="cell plan-header-cell">
              <div className="plan-header-content">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <h5 className="plan-title" style={{ margin: 0 }}>{plan.title}</h5>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {plan.isPopular && <div className="popular-badge popular-badge--inline">Most Popular</div>}
                  </div>
                </div>
                <div className="plan-price-container">
                  <div className="plan-price-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span className="plan-price">{formatPriceDisplay(plan.price)}</span>
                      {!isFreePrice(plan.price) && <span className="plan-period">/month</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {plan.id === currentPlanId && currentSubscriptionStatus && currentSubscriptionStatus.toLowerCase().includes('cancel') && currentSubscriptionEndDate && (
                        <span className="cancel-tag">
                          Ends on: {new Date(currentSubscriptionEndDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      )}
                      {(() => {
                        const trialInfo = getCurrentTrialInfoForPlan(plan);
                        if (trialInfo) {
                          // User is actively on trial - show remaining days
                          return (
                            <span className="trial-badge">
                              Trial left {trialInfo.daysLeft} day{trialInfo.daysLeft !== 1 ? 's' : ''}
                            </span>
                          );
                        }
                        // User hasn't taken trial yet - show original "X Days Free Trial" format
                        return (
                          !isFreePrice(plan.price) &&
                          plan.eligibleFreeTrial &&
                          Number(plan.freeTrialDays) > 0 && (
                            <span className="trial-badge">
                              {Number(plan.freeTrialDays)} Day{Number(plan.freeTrialDays) > 1 ? 's' : ''} Free Trial
                            </span>
                          )
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <Button
                  className={`plan-button ${plan.buttonText === 'Upgrade' || plan.buttonText === 'Current Plan' || plan.buttonText === 'Free Plan' ? 'plan-button--upgrade' : ''}`}
                  onClick={plan.buttonAction}
                  disabled={plan.isDisabled || loadingStates[plan.id] || !stripeLoaded}
                >
                  {loadingStates[plan.id] ? 'Processing...' : plan.buttonText}
                </Button>
                {/* Cancel button removed from individual plan card (centralized in profile page) */}
                {/* <Button
  className="plan-button"
  disabled
>
  {plan.buttonText}
</Button> */}
              </div>

              {/* Benefits section for mobile - will be hidden on desktop */}
              <div className="plan-benefits">
                {allBenefitNames.map((benefitName, benefitIdx) => {
                  const benefit = findBenefitForPlan(plan, benefitName);
                  return (
                    <div key={benefitIdx} className="plan-benefit-item">
                      <span className={`benefit-icon ${benefit.state}`}>
                        {benefit.state === 'positive' ? (
                          <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M15.9422 1.06719L5.94219 11.0672C5.82496 11.1845 5.66588 11.2505 5.5 11.2505C5.33412 11.2505 5.17504 11.1845 5.05781 11.0672L0.682812 6.69219C0.438599 6.44797 0.438599 6.05203 0.682812 5.80781C0.927026 5.5636 1.32297 5.5636 1.56719 5.80781L5.5 9.74141L15.0578 0.182813C15.302 -0.0614009 15.698 -0.0614009 15.9422 0.182813C16.1864 0.427026 16.1864 0.822974 15.9422 1.06719Z" fill="#4CAF50" />
                          </svg>
                        ) : (
                          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" clipRule="evenodd" d="M13.2071 3.79289C13.5976 4.18342 13.5976 4.81658 13.2071 5.20711L10.4142 8L13.2071 10.7929C13.5976 11.1834 13.5976 11.8166 13.2071 12.2071C12.8166 12.5976 12.1834 12.5976 11.7929 12.2071L9 9.41421L6.20711 12.2071C5.81658 12.5976 5.18342 12.5976 4.79289 12.2071C4.40237 11.8166 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 4.18342 4.79289 3.79289C5.18342 3.40237 5.81658 3.40237 6.20711 3.79289L9 6.58579L11.7929 3.79289C12.1834 3.40237 12.8166 3.40237 13.2071 3.79289Z" fill="#F44336" />
                          </svg>
                        )}
                      </span>
                      {renderBenefitValueWithTooltips(benefit.benefit_value)}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Benefit Rows */}
          {allBenefitNames.map((benefitName, rowIdx) => (
            <React.Fragment key={rowIdx}>
              <div className="cell feature-name" style={{ position: 'relative', display: 'inline-block', minWidth: '205px' }}>
                {benefitName}
                {isComingSoonFeature(benefitName, findBenefitForPlan(plans[0], benefitName)) && (
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                    color: 'white',
                    fontSize: '9px',
                    fontWeight: 600,
                    padding: '1px 5px 1px 2px',
                    borderRadius: '3px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    boxShadow: '0 1px 3px rgba(255, 107, 53, 0.25)',
                    whiteSpace: 'nowrap'
                  }}>
                    Coming Soon
                  </div>
                )}
              </div>


              {plans.map((plan) => {
                const benefit = findBenefitForPlan(plan, benefitName);
                return (
                  <div key={`${plan.id}-${benefitName}`} className="cell plan-benefit-cell">
                    <span className={`benefit-icon ${benefit.state}`}>
                      {benefit.state === 'positive' ? (
                        <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M15.9422 1.06719L5.94219 11.0672C5.82496 11.1845 5.66588 11.2505 5.5 11.2505C5.33412 11.2505 5.17504 11.1845 5.05781 11.0672L0.682812 6.69219C0.438599 6.44797 0.438599 6.05203 0.682812 5.80781C0.927026 5.5636 1.32297 5.5636 1.56719 5.80781L5.5 9.74141L15.0578 0.182813C15.302 -0.0614009 15.698 -0.0614009 15.9422 0.182813C16.1864 0.427026 16.1864 0.822974 15.9422 1.06719Z" fill="#4CAF50" />
                        </svg>
                      ) : (
                        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" clipRule="evenodd" d="M13.2071 3.79289C13.5976 4.18342 13.5976 4.81658 13.2071 5.20711L10.4142 8L13.2071 10.7929C13.5976 11.1834 13.5976 11.8166 13.2071 12.2071C12.8166 12.5976 12.1834 12.5976 11.7929 12.2071L9 9.41421L6.20711 12.2071C5.81658 12.5976 5.18342 12.5976 4.79289 12.2071C4.40237 11.8166 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 4.18342 4.79289 3.79289C5.18342 3.40237 5.81658 3.40237 6.20711 3.79289L9 6.58579L11.7929 3.79289C12.1834 3.40237 12.8166 3.40237 13.2071 3.79289Z" fill="#F44336" />
                        </svg>
                      )}
                    </span>
                    {renderBenefitValueWithTooltips(benefit.benefit_value)}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Mobile: features + horizontally scrollable one-plan column (no See more) */}
      {isMobile && (
        <>
          <div className="mobile-compare">
            <div className="mobile-features-col">
              <div className="mobile-feature-header">Features</div>
              <div className="mobile-feature-list">
                {allBenefitNames.map((benefitName, idx) => (
                  <div key={idx} className="mobile-feature-item" style={{ position: 'relative', paddingRight: '60px', display: 'inline-block' }}>
                    {benefitName}
                    {isComingSoonFeature(benefitName, findBenefitForPlan(plans[0], benefitName)) && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                        color: 'white',
                        fontSize: '7px',
                        fontWeight: 600,
                        padding: '0px 2px',
                        borderRadius: '3px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        boxShadow: '0 1px 1px rgba(255, 107, 53, 0.15)'
                      }}>
                        Coming Soon
                      </div>
                    )}
                  </div>


                ))}
              </div>
            </div>

            <div className="mobile-plans-scroll">
              {plans.map((plan) => (
                <div className="mobile-plan-col" key={plan.id}>
                  <div className="mobile-plan-header">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div className="plan-title" style={{ fontSize: 16 }}>{plan.title}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {plan.isPopular && <div className="popular-badge popular-badge--inline">Most Popular</div>}
                      </div>
                    </div>
                    <div className="plan-price-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                        <span className="plan-price">{formatPriceDisplay(plan.price)}</span>
                        {!isFreePrice(plan.price) && <span className="plan-period">/month</span>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {plan.id === currentPlanId && currentSubscriptionStatus && currentSubscriptionStatus.toLowerCase().includes('cancel') && currentSubscriptionEndDate && (
                          <span className="cancel-tag">
                            Ends on: {new Date(currentSubscriptionEndDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        )}
                        {(() => {
                          const trialInfo = getCurrentTrialInfoForPlan(plan);
                          if (trialInfo) {
                            // User is actively on trial - show remaining days
                            return (
                              <span className="trial-badge">
                                Trial left {trialInfo.daysLeft} day{trialInfo.daysLeft !== 1 ? 's' : ''}
                              </span>
                            );
                          }
                          // User hasn't taken trial yet - show original "X Days Free Trial" format
                          return (
                            !isFreePrice(plan.price) &&
                            plan.eligibleFreeTrial &&
                            Number(plan.freeTrialDays) > 0 && (
                              <span className="trial-badge">
                                {Number(plan.freeTrialDays)} Day{Number(plan.freeTrialDays) > 1 ? 's' : ''} Free Trial
                              </span>
                            )
                          );
                        })()}
                      </div>
                    </div>
                    <Button
                      className={`plan-button ${plan.buttonText === 'Upgrade' || plan.buttonText === 'Current Plan' || plan.buttonText === 'Free Plan' ? 'plan-button--upgrade' : ''}`}
                      onClick={plan.buttonAction}
                      disabled={plan.isDisabled || loadingStates[plan.id] || !stripeLoaded}
                    >
                      {loadingStates[plan.id] ? 'Processing...' : plan.buttonText}
                    </Button>
                    {/* Cancel button removed from individual plan card (centralized in profile page) */}
                  </div>

                  <div className="mobile-benefits-col">
                    {allBenefitNames.map((benefitName, bIdx) => {
                      const benefit = findBenefitForPlan(plan, benefitName);
                      return (
                        <div key={bIdx} className="mobile-benefit-row">
                          <span className={`benefit-icon ${benefit.state}`}>
                            {benefit.state === 'positive' ? (
                              <svg width="17" height="12" viewBox="0 0 17 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M15.9422 1.06719L5.94219 11.0672C5.82496 11.1845 5.66588 11.2505 5.5 11.2505C5.33412 11.2505 5.17504 11.1845 5.05781 11.0672L0.682812 6.69219C0.438599 6.44797 0.438599 6.05203 0.682812 5.80781C0.927026 5.5636 1.32297 5.5636 1.56719 5.80781L5.5 9.74141L15.0578 0.182813C15.302 -0.0614009 15.698 -0.0614009 15.9422 0.182813C16.1864 0.427026 16.1864 0.822974 15.9422 1.06719Z" fill="#4CAF50" />
                              </svg>
                            ) : (
                              <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M13.2071 3.79289C13.5976 4.18342 13.5976 4.81658 13.2071 5.20711L10.4142 8L13.2071 10.7929C13.5976 11.1834 13.5976 11.8166 13.2071 12.2071C12.8166 12.5976 12.1834 12.5976 11.7929 12.2071L9 9.41421L6.20711 12.2071C5.81658 12.5976 5.18342 12.5976 4.79289 12.2071C4.40237 11.8166 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 11.1834 4.79289 10.7929L7.58579 8L4.79289 5.20711C4.40237 4.81658 4.40237 4.18342 4.79289 3.79289C5.18342 3.40237 5.81658 3.40237 6.20711 3.79289L9 6.58579L11.7929 3.79289C12.1834 3.40237 12.8166 3.40237 13.2071 3.79289Z" fill="#F44336" />
                              </svg>
                            )}
                          </span>
                          {renderBenefitValueWithTooltips(benefit.benefit_value)}
                        </div>
                      );
                    })}
                  </div>

                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Cancellation Confirmation Modal */}
      {showCancelModal && cancelPlanData && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: '#171821',
            border: '1px solid rgba(53, 55, 72, 1)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            color: '#ffffff'
          }}>
            <h4 style={{ marginBottom: '16px', color: '#ffffff' }}>
              Cancel Subscription
            </h4>
            <p style={{ marginBottom: '24px', color: 'rgba(161, 172, 184, 1)' }}>
              Are you sure you want to cancel your <strong>{cancelPlanData.planName}</strong> subscription?
            </p>
            <p style={{ marginBottom: '24px', color: 'rgba(161, 172, 184, 1)', fontSize: '14px' }}>
              You will continue to have access until the end of your current billing period.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <Button
                color="secondary"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelPlanData(null);
                }}
                style={{ minWidth: '80px' }}
              >
                Keep Plan
              </Button>
              <Button
                color="danger"
                className="cancel-action-button"
                onClick={() => handleCancelSubscription(cancelPlanData.subscriptionId, cancelPlanData.planName)}
                disabled={loadingStates[`cancel_${cancelPlanData.subscriptionId}`]}
                style={{ minWidth: '80px' }}
              >
                {loadingStates[`cancel_${cancelPlanData.subscriptionId}`] ? 'Cancelling...' : 'Cancel Plan'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal */}
      {showUpgradeModal && upgradePlanData && (
        <UpgradePlanModal
          show={showUpgradeModal}
          onHide={() => {
            setShowUpgradeModal(false);
            setUpgradePlanData(null);
          }}
          currentPlan={upgradePlanData.currentPlan}
          targetPlan={upgradePlanData.targetPlan}
          preview={upgradePlanData.preview}
          onConfirm={handlePlanUpgrade}
          loading={loadingStates[`upgrade_${upgradePlanData.targetPlan.id}`]}
        />
      )}
    </div>
  );
}
